import PropTypes from "prop-types"
import React from "react"
import { useMarina } from "src/contexts/MarinaContext"
import { countedCopy, fillCopy } from "src/main/Billing/PayInvoices/copy"

import Button from "src/components/Button"
import Form from "src/components/Form"
import Tooltip from "src/components/Tooltip"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"

import { isPayable } from "./helpers"

const withDisabledTooltip = (button, text) => (
  <Tooltip text={text} placement="bottom" variant="dark">
    <div className="cursor-not-allowed">{button}</div>
  </Tooltip>
)

const ContactInvoicesActions = ({
  selectionCount,
  selectedInvoices,
  isSelectionLoading,
  isSelectionError,
  hasOpenInvoices,
  isPayableListLoaded,
  maxInvoicesPerPayment,
  copy,
  onExportAll,
  onExportSelected,
  onPayOpenInvoices,
  onPaySelected,
}) => {
  const { presentmentCurrency } = useMarina()

  if (selectionCount === 0) {
    const payOpenButton = (
      <Button
        variant="primary"
        disabled={!hasOpenInvoices}
        onClick={onPayOpenInvoices}
      >
        {copy.entryPoints.payOpenInvoices}
      </Button>
    )

    return (
      <div className="flex items-center gap-2">
        <Button variant="secondary" onClick={onExportAll}>
          {copy.entryPoints.exportAll}
        </Button>
        {isPayableListLoaded && !hasOpenInvoices
          ? withDisabledTooltip(payOpenButton, copy.noOpenInvoices)
          : payOpenButton}
      </div>
    )
  }

  const openInvoices = selectedInvoices.filter(isPayable)
  const isOverMax = openInvoices.length > maxInvoicesPerPayment
  const isAllPaid = openInvoices.length === 0
  const dueAmount = openInvoices.reduce(
    (sum, invoice) => sum + invoice.balance,
    0
  )

  const payButton = (
    <Button
      variant="primary"
      disabled={
        isAllPaid || isOverMax || isSelectionLoading || isSelectionError
      }
      onClick={() => onPaySelected(openInvoices)}
    >
      {isAllPaid
        ? copy.entryPoints.payNoOpenInvoices
        : countedCopy(copy.entryPoints.payInvoices, openInvoices.length)}
    </Button>
  )

  const disabledReason = isSelectionError
    ? copy.selectionLoadFailed
    : isAllPaid &&
      !isSelectionLoading &&
      countedCopy(copy.alreadyPaid, selectionCount)

  return (
    <div className="flex items-center gap-2">
      {isOverMax && (
        <Form.Error>
          {fillCopy(copy.maxInvoices, { max: maxInvoicesPerPayment })}
        </Form.Error>
      )}
      <span className="whitespace-nowrap px-2 text-sm text-gray-900">
        {fillCopy(copy.entryPoints.selectionSummary, {
          count: selectionCount,
          amount: formatCurrencyFromMinorUnits({
            amount: dueAmount,
            currency: presentmentCurrency,
          }),
        })}
      </span>
      <Button variant="secondary" onClick={onExportSelected}>
        {countedCopy(copy.entryPoints.exportInvoices, selectionCount)}
      </Button>
      {disabledReason
        ? withDisabledTooltip(payButton, disabledReason)
        : payButton}
    </div>
  )
}

const countedFormsPropType = PropTypes.shape({
  one: PropTypes.string.isRequired,
  other: PropTypes.string.isRequired,
})

ContactInvoicesActions.propTypes = {
  selectionCount: PropTypes.number.isRequired,
  selectedInvoices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      balance: PropTypes.number.isRequired,
      status: PropTypes.string.isRequired,
      totalAmount: PropTypes.number.isRequired,
    })
  ).isRequired,
  isSelectionLoading: PropTypes.bool.isRequired,
  isSelectionError: PropTypes.bool.isRequired,
  hasOpenInvoices: PropTypes.bool.isRequired,
  isPayableListLoaded: PropTypes.bool.isRequired,
  maxInvoicesPerPayment: PropTypes.number.isRequired,
  copy: PropTypes.shape({
    alreadyPaid: countedFormsPropType.isRequired,
    entryPoints: PropTypes.shape({
      exportAll: PropTypes.string.isRequired,
      exportInvoices: countedFormsPropType.isRequired,
      payInvoices: countedFormsPropType.isRequired,
      payNoOpenInvoices: PropTypes.string.isRequired,
      payOpenInvoices: PropTypes.string.isRequired,
      selectionSummary: PropTypes.string.isRequired,
    }).isRequired,
    maxInvoices: PropTypes.string.isRequired,
    noOpenInvoices: PropTypes.string.isRequired,
    selectionLoadFailed: PropTypes.string.isRequired,
  }).isRequired,
  onExportAll: PropTypes.func.isRequired,
  onExportSelected: PropTypes.func.isRequired,
  onPayOpenInvoices: PropTypes.func.isRequired,
  onPaySelected: PropTypes.func.isRequired,
}

export default ContactInvoicesActions
